import { useState, type FormEvent } from "react";

const FORM_URL = "https://readdy.ai/api/form/daf9dp3ep053ij1lrg";

type Status = "idle" | "success" | "error";

export default function Newsletter() {
  const [status, setStatus] = useState<Status>("idle");
  const [formError, setFormError] = useState("");
  const [email, setEmail] = useState("");

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = String(fd.get("website_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      setEmail("");
      form.reset();
      return;
    }
    fd.delete("website_alt");

    setStatus("idle");
    setFormError("");
    try {
      const res = await fetch(FORM_URL, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: new URLSearchParams(fd as unknown as Record<string, string>).toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg = parsed?.meta?.message || parsed?.meta?.detail || responseText;
      const isOk = res.ok && parsed?.code === "OK";
      if (isOk) {
        setStatus("success");
        setEmail("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(serverMsg || "订阅失败，请稍后再试");
      }
    } catch {
      setStatus("error");
      setFormError("网络似乎开小差了，请稍后再试");
    }
  };

  return (
    <section id="subscribe" className="w-full bg-background-50 px-4 py-12 md:px-8 md:py-16 lg:px-14">
      <div className="relative mx-auto max-w-6xl overflow-hidden rounded-3xl bg-primary-600 px-6 py-12 md:px-12 md:py-16">
        <div className="absolute -right-16 -top-16 h-56 w-56 rounded-full bg-accent-500/25"></div>
        <div className="absolute -bottom-20 -left-10 h-64 w-64 rounded-full bg-background-950/15"></div>
        <div className="relative mx-auto max-w-2xl text-center">
          <span className="inline-flex items-center gap-2 rounded-full bg-background-50/15 px-4 py-1.5 text-xs font-medium text-background-50">
            <i className="ri-mail-send-fill text-accent-300" aria-hidden="true"></i> 每日热点精选 · 免费订阅
          </span>
          <h2 className="mt-5 font-heading text-2xl font-black leading-snug text-background-50 md:text-4xl">
            别让热点从指缝溜走
          </h2>
          <p className="mt-4 text-sm leading-relaxed text-background-100/90 md:text-base">
            每天一封邮件，把当日最值得关注的刷屏内容、出圈梗与事件复盘送到你邮箱。不发垃圾消息，随时可退订。
          </p>

          {status === "success" ? (
            <div className="mx-auto mt-7 flex max-w-md items-center justify-center gap-3 rounded-2xl border border-accent-300/40 bg-background-50/10 px-6 py-4 text-background-50">
              <i className="ri-checkbox-circle-fill text-accent-300 text-xl" aria-hidden="true"></i>
              <p className="text-sm">订阅成功！明天起，热点精选会准时送达你的邮箱。</p>
            </div>
          ) : (
            <form
              onSubmit={handleSubmit}
              data-readdy-form="订阅吃瓜爆网每日热点精选"
              className="mx-auto mt-8 flex max-w-lg flex-col gap-3 sm:flex-row"
            >
              <label htmlFor="newsletter-email" className="sr-only">
                邮箱地址
              </label>
              <input
                id="newsletter-email"
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="请输入你的邮箱地址"
                className="w-full rounded-full border border-background-50/30 bg-background-50/95 px-5 py-3 text-sm text-foreground-950 outline-none placeholder:text-foreground-400 focus:ring-2 focus:ring-accent-400"
              />
              <button
                type="submit"
                className="whitespace-nowrap rounded-full bg-accent-500 px-7 py-3 text-sm font-semibold text-foreground-950 transition-colors hover:bg-accent-400"
              >
                立即订阅
              </button>
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="hp-field"
              />
            </form>
          )}

          {status === "error" && (
            <p className="mx-auto mt-4 max-w-md rounded-xl bg-background-50/15 px-4 py-2.5 text-sm text-accent-100" role="alert">
              {formError}
            </p>
          )}
          <p className="mt-4 text-xs text-background-100/70">已订阅读者对内容满意度达 94%，点击退订随时离开。</p>
        </div>
      </div>
    </section>
  );
}